import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * PartnerBanner
 * ─────────────────────────────────────────────────────────────────────
 * Contextual, native-sized partner banner. Only renders when a supplier
 * is topically relevant to the page (backend decides), so banners never
 * appear as blanket ads. Kept at the creative's real dimensions.
 *
 * props: size (leaderboard | full_banner | medium_rectangle | square |
 *              skyscraper | wide_skyscraper), topic (page slug/keyword),
 *        className.
 */
const PartnerBanner = ({ size = 'leaderboard', topic = '', supplier = '', showCaption = true, stretch = false, className = '', onResolve }) => {
  const [b, setB] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const qs = new URLSearchParams({ size, topic });
        if (supplier) qs.set('supplier', supplier);
        const r = await fetch(`${API}/api/affiliate/banner?${qs.toString()}`);
        if (!r.ok) { if (!cancelled) { setB(null); onResolve?.(false); } return; }
        const j = await r.json();
        if (!cancelled) { setB(j.banner || null); onResolve?.(Boolean(j.banner)); }
      } catch { if (!cancelled) { setB(null); onResolve?.(false); } }
    })();
    return () => { cancelled = true; };
  }, [size, topic, supplier]);

  if (!b) return null;

  // Never stretch a portrait/skyscraper creative to fill a horizontal slot —
  // that squashes it. Portrait creatives always render at natural proportions,
  // centred, regardless of the stretch request.
  const portrait = b.h > b.w;
  const useStretch = stretch && !portrait;

  return (
    <div className={`flex flex-col items-center gap-1.5 ${className}`} data-testid="partner-banner">
      <a
        href={`${API}${b.click_url}`}
        target="_blank"
        rel="nofollow sponsored noopener noreferrer"
        referrerPolicy="no-referrer-when-downgrade"
        className={`block leading-none ${useStretch ? 'w-full' : 'max-w-full'}`}
        data-testid={`partner-banner-${b.id}`}
        aria-label={`${b.supplier} (sponsored)`}
      >
        <img
          src={`${API}${b.image_url}`}
          width={b.w}
          height={b.h}
          loading="lazy"
          decoding="async"
          alt={`${b.supplier}`}
          referrerPolicy="no-referrer-when-downgrade"
          className={`block h-auto rounded-lg ${useStretch ? 'w-full' : 'max-w-full'}`}
          style={{ aspectRatio: `${b.w} / ${b.h}` }}
        />
      </a>
      {showCaption && (
        <p className="text-[10px] uppercase tracking-[0.05em] text-[#b0b4bb]">
          Sponsored · <Link to="/affiliate-disclosure" className="underline hover:text-[#6b7078]">why?</Link>
        </p>
      )}
    </div>
  );
};

export default PartnerBanner;
